import { ChangeDetectionStrategy, Component, computed, viewChild } from '@angular/core';
import { FormlyModule } from '@ngx-formly/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs/operators';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FormlyErrorComponent } from '../formly-error.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';

/**
 * Curated Tailwind palette: 17 hues × 9 shades (100-900) + neutral slate row.
 * Order warm → cool → neutral, same structure as Tailwind's own palette docs
 * so the grid looks visually correct.
 *
 * Shades 100-900 cover the full usable spectrum: pastels on the left for
 * background tints, deep tones on the right for text/accents. 50 is omitted
 * because it is barely distinguishable on a white UI.
 */
const PRESET_PALETTE: ReadonlyArray<ReadonlyArray<string>> = [
  ['#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'], // red
  ['#ffedd5', '#fed7aa', '#fdba74', '#fb923c', '#f97316', '#ea580c', '#c2410c', '#9a3412', '#7c2d12'], // orange
  ['#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b', '#d97706', '#b45309', '#92400e', '#78350f'], // amber
  ['#fef9c3', '#fef08a', '#fde047', '#facc15', '#eab308', '#ca8a04', '#a16207', '#854d0e', '#713f12'], // yellow
  ['#ecfccb', '#d9f99d', '#bef264', '#a3e635', '#84cc16', '#65a30d', '#4d7c0f', '#3f6212', '#365314'], // lime
  ['#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d', '#166534', '#14532d'], // green
  ['#d1fae5', '#a7f3d0', '#6ee7b7', '#34d399', '#10b981', '#059669', '#047857', '#065f46', '#064e3b'], // emerald
  ['#ccfbf1', '#99f6e4', '#5eead4', '#2dd4bf', '#14b8a6', '#0d9488', '#0f766e', '#115e59', '#134e4a'], // teal
  ['#cffafe', '#a5f3fc', '#67e8f9', '#22d3ee', '#06b6d4', '#0891b2', '#0e7490', '#155e75', '#164e63'], // cyan
  ['#e0f2fe', '#bae6fd', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7', '#0369a1', '#075985', '#0c4a6e'], // sky
  ['#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a'], // blue
  ['#e0e7ff', '#c7d2fe', '#a5b4fc', '#818cf8', '#6366f1', '#4f46e5', '#4338ca', '#3730a3', '#312e81'], // indigo
  ['#ede9fe', '#ddd6fe', '#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9', '#5b21b6', '#4c1d95'], // violet
  ['#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7e22ce', '#6b21a8', '#581c87'], // purple
  ['#fae8ff', '#f5d0fe', '#f0abfc', '#e879f9', '#d946ef', '#c026d3', '#a21caf', '#86198f', '#701a75'], // fuchsia
  ['#fce7f3', '#fbcfe8', '#f9a8d4', '#f472b6', '#ec4899', '#db2777', '#be185d', '#9d174d', '#831843'], // pink
  ['#ffe4e6', '#fecdd3', '#fda4af', '#fb7185', '#f43f5e', '#e11d48', '#be123c', '#9f1239', '#881337'], // rose
  ['#f1f5f9', '#e2e8f0', '#cbd5e1', '#94a3b8', '#64748b', '#475569', '#334155', '#1e293b', '#0f172a'], // slate (neutral)
];

/** 3- of 6-char hex, met of zonder #. Vangt eigen invoer voorzichtig op. */
const HEX_PATTERN = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Normalizes a hex string to 6-char lowercase with # prefix.
 * `abc` / `#abc` → `#aabbcc`. Requires input to match HEX_PATTERN.
 */
function normalizeHex(raw: string): string {
  const value = raw.trim().toLowerCase();
  const withHash = value.startsWith('#') ? value : '#' + value;
  if (withHash.length === 4) {
    return '#' + withHash[1] + withHash[1] + withHash[2] + withHash[2] + withHash[3] + withHash[3];
  }
  return withHash;
}

@Component({
  selector: 'formly-mat-color-picker-input',
  template: `
    <mat-form-field
      class="w-full"
      [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
      <mat-label class="font-medium">
        <app-field-label-row
          [label]="props.label + (props.required ? ' *' : '')"
          [required]="props.required"
          [info]="props.info"></app-field-label-row>
      </mat-label>

      <!-- Swatch-preview als prefix; klikbaar om popover te openen -->
      <button
        matPrefix
        type="button"
        class="color-swatch-preview ml-2 mr-2"
        [class.color-swatch-empty]="!formControl.value"
        [style.background]="formControl.value || null"
        [matMenuTriggerFor]="palettePopover"
        (menuOpened)="onMenuOpened()"
        [disabled]="props.disabled"
        [attr.aria-label]="formControl.value ? 'Kies kleur, huidige: ' + formControl.value : 'Kies een kleur'"></button>

      <!-- Readonly hex-weergave; click opent dezelfde popover als swatch -->
      <!-- Opens the popover through the swatch's trigger instead of carrying matMenuTriggerFor
           itself: the directive host-binds aria-expanded/aria-haspopup, which a plain textbox may
           not have (axe: aria-allowed-attr). The keyboard routes are spelled out here because the
           directive was also what provided them - without these three a keyboard user could focus
           the hex field and had no way left to open the palette. -->
      <input
        #colorInput
        matInput
        type="text"
        readonly
        [value]="formControl.value || ''"
        (click)="openPalette()"
        (keydown.enter)="openPalette($event)"
        (keydown.space)="openPalette($event)"
        (keydown.arrowdown)="openPalette($event)"
        [disabled]="props.disabled"
        [placeholder]="props.placeholder ?? 'Kies een kleur'"
        [formlyDataCy]="field" />

      <app-clear-field-button
        matSuffix
        [field]="field"
        [hasValue]="hasClearableValue()"
        [disabled]="isDisabled()"
        [readOnly]="isReadOnly()"
        [ariaLabel]="clearAriaLabel()"
        [focusTarget]="colorInput"
        (clearSelection)="clear()" />

      @if (field.props.description) {
        <mat-hint>{{ field.props.description }}</mat-hint>
      }
      <app-formly-error [field]="field"></app-formly-error>

      <!-- Popover: preset-grid + custom hex-input -->
      <mat-menu #palettePopover="matMenu" class="color-palette-menu" xPosition="after">
        <!-- stopPropagation op de container: voorkomt dat mat-menu sluit
             tijdens typen in de hex-input. Swatch-klikken sluiten wél, want
             die roepen daarna pickPreset() → trigger.closeMenu(). -->
        <div class="color-palette-grid" (click)="$event.stopPropagation()">
          @for (row of palette; track $index) {
            <div class="color-palette-row">
              @for (hex of row; track hex) {
                <button
                  type="button"
                  class="color-swatch"
                  [class.color-swatch-selected]="isSelected(hex)"
                  [style.background]="hex"
                  [attr.aria-label]="hex"
                  [matTooltip]="hex"
                  matTooltipShowDelay="500"
                  (click)="pickPreset(hex)"></button>
              }
            </div>
          }
          <div class="color-palette-hex-row">
            <mat-form-field class="hex-field" appearance="outline" subscriptSizing="dynamic">
              <mat-label>Hex</mat-label>
              <input
                matInput
                type="text"
                placeholder="#3b82f6"
                maxlength="7"
                [formControl]="hexInputControl"
                (keydown.enter)="applyHexInput(); $event.preventDefault()" />
            </mat-form-field>
            <button
              type="button"
              mat-stroked-button
              color="primary"
              [disabled]="!isValidHexInput()"
              (click)="applyHexInput()">
              Toepassen
            </button>
          </div>
        </div>
      </mat-menu>
    </mat-form-field>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .color-swatch-preview {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      padding: 0;
      cursor: pointer;
    }
    /* "Leeg" swatch: dambord-patroon met rood diagonaal kruis — duidelijk
       signaal "geen kleur gekozen" zonder een misleidende effen kleur. */
    .color-swatch-empty {
      background:
        linear-gradient(45deg, transparent 45%, #ef4444 45%, #ef4444 55%, transparent 55%),
        repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 50% / 8px 8px !important;
    }
    .color-swatch-preview:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    ::ng-deep .color-palette-menu .mat-mdc-menu-content {
      padding: 12px;
    }
    .color-palette-grid {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }
    .color-palette-row {
      display: flex;
      gap: 3px;
    }
    .color-swatch {
      width: 22px;
      height: 22px;
      border-radius: 4px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      cursor: pointer;
      padding: 0;
      transition: transform 100ms ease, border-color 100ms ease;
    }
    .color-swatch:hover {
      transform: scale(1.15);
      border-color: rgba(0, 0, 0, 0.35);
    }
    .color-swatch-selected {
      outline: 2px solid var(--fuse-primary, #1e40af);
      outline-offset: 2px;
    }
    .color-palette-hex-row {
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px solid rgba(0, 0, 0, 0.08);
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .hex-field {
      flex: 1;
    }
  `,
  imports: [
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyDataCyDirective,
    FormlyErrorComponent,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
})
export class FormlyMatColorPickerInputComponent extends ClearableFieldType {
  readonly palette = PRESET_PALETTE;
  readonly hexInputControl = new FormControl<string>('', { nonNullable: true });
  protected readonly trigger = viewChild(MatMenuTrigger);

  /**
   * Opens the palette popover from the hex field, which is no longer a trigger element itself.
   * The event is passed for the keyboard routes only: space would scroll the page and arrow-down
   * would move the caret, and neither should happen while the palette opens.
   */
  protected openPalette(event?: Event): void {
    event?.preventDefault();
    this.trigger()?.openMenu();
  }

  readonly #hexValue = toSignal(this.hexInputControl.valueChanges.pipe(startWith('')), {
    initialValue: '',
  });
  protected readonly isValidHexInput = computed(() => HEX_PATTERN.test(this.#hexValue() ?? ''));

  protected isSelected(hex: string): boolean {
    return (this.formControl.value ?? '').toLowerCase() === hex.toLowerCase();
  }

  protected pickPreset(hex: string): void {
    this.commitColor(hex);
    this.trigger()?.closeMenu();
  }

  protected applyHexInput(): void {
    const raw = this.hexInputControl.value;
    if (!HEX_PATTERN.test(raw)) return;
    this.commitColor(normalizeHex(raw));
    this.hexInputControl.setValue('');
    this.trigger()?.closeMenu();
  }

  protected onMenuOpened(): void {
    const current = this.formControl.value;
    this.hexInputControl.setValue(typeof current === 'string' && HEX_PATTERN.test(current) ? current : '');
  }

  #commitInProgress = false;
  private commitColor(hex: string): void {
    if (this.#commitInProgress) return;
    this.#commitInProgress = true;
    this.formControl.setValue(hex);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.#commitInProgress = false;
  }
}
